import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Fade from 'react-reveal/Fade';

import { ThemeContext } from '../../contexts/ThemeContext';

import expImgWhite from '../../assets/svg/experience/expImgWhite.svg';
import expImgBlack from '../../assets/svg/experience/expImgBlack.svg';

import './Experience.css';

function ExperienceCard({
    id,
    company,
    jobtitle,
    startYear,
    endYear,
    details = [],
    roles = [],
    technologies,
    link,
}) {
    const { theme } = useContext(ThemeContext);

    const useStyles = makeStyles((t) => ({
        experienceCard: {
            backgroundColor: theme.primary30,
            '&:hover': {
                backgroundColor: theme.primary50,
            },
        },
        detailPanel: {
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translate(100%, -50%)',
            width: 'min(400px, 88vw)',
            minHeight: '200px',
            maxHeight: '70vh',
            overflowY: 'auto',
            padding: '1.25rem 1.5rem',
            borderRadius: '0 12px 12px 0',
            boxShadow: theme.type === 'dark'
                ? '4px 4px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)'
                : '4px 4px 24px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.08)',
            backgroundColor: theme.primary,
            color: theme.secondary,
            fontFamily: 'var(--primaryFont)',
            zIndex: 50,
            opacity: 0,
            pointerEvents: 'none',
            transition: 'transform 0.5s ease, opacity 0.35s ease',
            isolation: 'isolate',
            backfaceVisibility: 'hidden',
            [t.breakpoints.down('sm')]: {
                width: 'min(320px, 92vw)',
                maxHeight: '60vh',
            },
        },
        detailPanelSection: {
            marginBottom: '1rem',
            '&:last-child': { marginBottom: 0 },
        },
        detailRoleTitle: {
            fontSize: '0.95rem',
            fontWeight: 700,
            color: theme.secondary,
            marginBottom: '0.5rem',
            lineHeight: 1.3,
        },
        detailBullet: {
            fontSize: '0.875rem',
            lineHeight: 1.5,
            marginBottom: '0.4rem',
            paddingLeft: '1rem',
            position: 'relative',
            '&::before': {
                content: '"•"',
                position: 'absolute',
                left: 0,
                color: theme.secondary,
                fontWeight: 700,
            },
        },
        detailTech: {
            fontSize: '0.8rem',
            fontWeight: 600,
            color: theme.secondary,
            opacity: 0.95,
            marginTop: '0.5rem',
            lineHeight: 1.4,
        },
        detailLink: {
            display: 'inline-block',
            marginTop: '0.5rem',
            fontSize: '0.85rem',
            fontWeight: 600,
            color: theme.secondary,
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
    }));

    const classes = useStyles();
    const hasContent = (roles && roles.length > 0) || (details && details.length > 0) || technologies || link;

    return (
        <Fade bottom>
            <div className="experience-card-wrapper">
                <div key={id} className={`experience-card ${classes.experienceCard}`}>
                    <div className="expcard-img" style={{ backgroundColor: theme.primary }}>
                        <img src={theme.type === 'light' ? expImgBlack : expImgWhite} alt="" />
                    </div>
                    <div className="experience-details">
                        <h6 style={{ color: theme.primary }}>{startYear}–{endYear}</h6>
                        <h4 style={{ color: theme.tertiary }}>{jobtitle}</h4>
                        <h5 style={{ color: theme.tertiary80 }}>{company}</h5>
                        {hasContent && (
                            <span className="experience-card-hint" style={{ color: theme.primary80 }}>
                                Hover for details
                            </span>
                        )}
                    </div>
                </div>

                {hasContent && (
                    <div
                        className={`experience-card-detail ${classes.detailPanel}`}
                        style={{
                            backgroundColor: theme.primary,
                            color: theme.secondary,
                        }}
                    >
                        {roles.length > 0 ? (
                            roles.map((role, idx) => (
                                <div key={idx} className={classes.detailPanelSection}>
                                    <div className={classes.detailRoleTitle}>{role.title}</div>
                                    {role.bullets && role.bullets.map((bullet, i) => (
                                        <div key={i} className={classes.detailBullet}>
                                            {bullet}
                                        </div>
                                    ))}
                                    {role.technologies && (
                                        <div className={classes.detailTech}>
                                            Technologies: {role.technologies}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <>
                                {details.length > 0 && (
                                    <div className={classes.detailPanelSection}>
                                        {details.map((bullet, i) => (
                                            <div key={i} className={classes.detailBullet}>
                                                {bullet}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {technologies && (
                                    <div className={classes.detailPanelSection}>
                                        <div className={classes.detailTech}>
                                            Technologies: {technologies}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        {link && (
                            <a
                                href={link}
                                target="_blank"
                                rel="noreferrer"
                                className={classes.detailLink}
                                onClick={(e) => e.stopPropagation()}
                            >
                                View project →
                            </a>
                        )}
                    </div>
                )}
            </div>
        </Fade>
    );
}

export default ExperienceCard;
